import { Component, ReactNode, useEffect, useCallback, useState, useRef } from 'react';
import { useImageStore } from '@/entities/image/store';
import Toolbar from '@/features/folder-select/Toolbar';
import ImageGrid from '@/widgets/gallery/ImageGrid';
import ImageViewer from '@/widgets/gallery/ImageViewer';
import ExifSidebar from '@/widgets/sidebar/ExifSidebar';
import LLMPanel from '@/features/exif-llm/LLMPanel';
import DiffModal from '@/features/exif-edit/DiffModal';
import DebugLogger from '@/widgets/logger/DebugLogger';
import Notifications from '@/shared/ui/Notifications';
import { isElectron, platform } from '@/shared/platform';

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen w-screen flex flex-col items-center justify-center bg-gray-900 text-red-400 p-10 font-mono">
          <h1 className="text-2xl font-bold mb-4">App Crashed</h1>
          <p className="text-sm whitespace-pre-wrap">{this.state.error?.message}</p>
          <p className="text-xs text-gray-500 mt-4">Check console for detailed stack trace.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  // Destructure everything at once to prevent ReferenceErrors
  const {
    loadLLMConfig,
    folderPath,
    loggerMode,
    setLoggerMode,
    showLLMPanel,
    viewingImagePath,
  } = useImageStore();

  const [sidebarWidth, setSidebarWidth] = useState(384);
  const isResizing = useRef(false);

  // instanceId is now initialized in the store with localStorage persistence,
  // so it survives React remounts and page reloads without a useEffect.

  useEffect(() => {
    // Synchronize instanceId from store to web platform adapter
    if (!isElectron && (platform as any).setInstanceId) {
      const id = useImageStore.getState().instanceId;
      if (id) {
        (platform as any).setInstanceId(id);
        useImageStore.getState().addLog(`Instance ID synchronized: ${id}`);
      }
    }
  }, []);
  useEffect(() => {
    if (loadLLMConfig) loadLLMConfig();
    useImageStore.getState().addLog('App Initialized');
    useImageStore.getState().addLog(`Version: ${import.meta.env.VITE_APP_VERSION}`);
    useImageStore.getState().addLog(`Platform: ${isElectron ? 'Electron (Desktop)' : 'Web (Browser)'}`);

    const unsubLog = window.electronAPI.on('log', (msg) => {
      useImageStore.getState().addLog(String(msg));
    });

    const unsubNotif = window.electronAPI.on('notification', (payload: { type: string; message: string }) => {
      if (payload && payload.type) {
        useImageStore.getState().addNotification(payload.type as any, payload.message);
      }
    });

    return () => {
      (unsubLog as any)?.();
      (unsubNotif as any)?.();
    };
  }, [loadLLMConfig]);

  const handleApply = useCallback(async () => {
    const s = useImageStore.getState();
    const filtered = s.getFilteredImages();
    const selectedPaths = [...s.selectedIndices].map(i => filtered[i]?.path).filter(Boolean);
    const allPaths = selectedPaths.length > 0 ? selectedPaths : filtered.map(i => i.path);
    const changes = s.getChangesForPaths(allPaths);
    if (changes.length === 0) {
      s.addNotification('warning', 'No staged changes to apply.');
      return;
    }
    s.addNotification('info', `Applying ${changes.length} changes...`);
    try {
      let results: Array<{ path: string; success: boolean; error?: string }>;

      if (isElectron) {
        results = await window.electronAPI.invoke('exif:apply', changes);
      } else {
        // Web mode - use platform adapter
        results = await platform.applyChanges(changes);
      }

      const success = results.filter((r: any) => r.success).length;
      const failed = results.filter((r: any) => !r.success);
      if (failed.length > 0) {
        failed.forEach((r: any) => {
          s.addLog(`[ERROR] Apply failed for ${r.path}: ${r.error || 'Unknown error'}`);
        });
      }
      s.addNotification('success', `Applied changes to ${success}/${results.length} files.`);
      s.resetStagedChanges();
    } catch (e: any) {
      s.addNotification('error', 'Apply failed: ' + e.message);
      s.addLog(`[ERROR] Apply failed: ${e.message}\nStack: ${e.stack || 'N/A'}\nChanges: ${JSON.stringify(changes.map(c => c.path))}`);
    }
  }, []);

  const handleOpenFolder = useCallback(async () => {
    const s = useImageStore.getState();

    if (isElectron) {
      // Electron: open folder dialog
      s.addLog('Opening folder dialog...');
      const path = await window.electronAPI.invoke('folder:select');
      if (!path) return;
      s.addLog(`Folder selected: ${path}`);
      s.setFolderPath(path);
      const imgs = await window.electronAPI.invoke('images:get', path);
      s.setImages(imgs || []);
      s.addLog(`Loaded ${imgs?.length || 0} images.`);
    } else {
      // Web mode: handled by Toolbar file input
      // This function shouldn't be called directly in web mode
      s.addLog('File upload triggered via browser file picker');
    }
  }, []);


  const isMaximized = loggerMode === 'maximized';

  const startResizing = useCallback(() => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const resize = useCallback((e: MouseEvent) => {
    if (!isResizing.current) return;
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth > 200 && newWidth < window.innerWidth * 0.7) {
      setSidebarWidth(newWidth);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-screen w-screen bg-zinc-950 text-zinc-100 overflow-hidden">
        {!isMaximized && (
          <>
            <Toolbar
              onOpenFolder={handleOpenFolder}
              onApply={handleApply}
              folderPath={folderPath}
            />
            <div className="flex flex-1 overflow-hidden relative">
              <div className="flex-1 overflow-y-auto p-4 bg-zinc-900 relative">
                {viewingImagePath ? <ImageViewer /> : <ImageGrid />}
              </div>
              <div
                className="w-1 cursor-col-resize bg-gray-700 hover:bg-blue-500 transition-colors shrink-0"
                onMouseDown={startResizing}
              />
              <div
                className="shrink-0 border-l border-gray-700 overflow-y-auto bg-gray-800"
                style={{ width: sidebarWidth }}
              >
                <ExifSidebar />
              </div>
            </div>
            {showLLMPanel && <LLMPanel />}
            <DiffModal />
            <Notifications />
          </>
        )}
        <DebugLogger mode={loggerMode} onModeChange={setLoggerMode} />
      </div>
    </ErrorBoundary>
  );
}
